/**
 * Aborted / unreachable HTTP requests (#13431, #13432).
 *
 * Angular's `HttpClient` reports every failure that never produced an HTTP
 * response as `HttpErrorResponse` with `status: 0` and the synthetic text
 * "Unknown Error":
 *
 *   Http failure response for /api/user/all-clients: 0 Unknown Error
 *
 * Status 0 means the browser aborted the XHR — tab closed, navigation away
 * mid-flight, sleep/offline, or a dropped connection. There is no server-side
 * failure behind it: nginx logs the same requests as 499 ("client closed
 * request"), and production shows zero 5xx for these endpoints. Reporting them
 * as unhandled exceptions is noise, so they are logged to the console and
 * dropped before reaching Sentry.
 *
 * Genuine server failures still carry a real status code (4xx/5xx) and are
 * unaffected.
 */

function unwrap(error: unknown): unknown {
  if (error == null || typeof error !== 'object') {
    return error;
  }
  const obj = error as Record<string, unknown>;
  // Zone.js wraps promise rejections in `rejection`; Angular's ErrorHandler
  // keeps the pre-wrapping error on `ngOriginalError`.
  return obj['rejection'] ?? obj['ngOriginalError'] ?? error;
}

export function isAbortedHttpRequestError(error: unknown): boolean {
  const candidate = unwrap(error);
  if (candidate == null || typeof candidate !== 'object') {
    return false;
  }

  const obj = candidate as Record<string, unknown>;
  return obj['name'] === 'HttpErrorResponse' && obj['status'] === 0;
}
